import React from "react"
import { useSelector } from "react-redux"
import { getInsuranceSyncInfo } from "reducers/reimbursementDetails"
import { COMPANY_DEDUCTIBLE_STATUS, CSD_CONFIGURATION, useUserDeductible } from "../user/DeductibleContext"
import { useCurrentUser } from "../user/UserContext"

const BenefitEnrollmentContext = React.createContext(null)

export const useBenefitEnrollment = () => React.useContext(BenefitEnrollmentContext)

export function BenefitEnrollmentProvider({ children }): JSX.Element {
  const {
    memberCoverageStartDate,
    memberCoverageEndDate,
    partnerCoverageStartDate,
    partnerCoverageEndDate,
    memberHasLegalPartner,
    memberPlanSourceSelectValue,
    partnerPlanSourceSelectValue
  } = useCurrentUser()

  const { deductible, csdConfiguration, companyDeductibleStatus } = useUserDeductible()
  const sourceName = deductible?.sourceName
  const memberHasInsurance = memberPlanSourceSelectValue !== "NOT_INSURED"
  const partnerHasInsurance = memberHasLegalPartner && partnerPlanSourceSelectValue !== "NOT_INSURED"
  const companyHasExternalDeductible = companyDeductibleStatus === COMPANY_DEDUCTIBLE_STATUS.EXTERNAL
  const insuranceSyncInfo = useSelector(getInsuranceSyncInfo)

  const deductibleFlowType = ((): string => {
    if (!memberHasInsurance) {
      return "NO_DEDUCTIBLE"
    }
    if (companyHasExternalDeductible) {
      return "EXTERNAL"
    }
    if (companyDeductibleStatus === COMPANY_DEDUCTIBLE_STATUS.CARROT_SPECIFIC_DEDUCTIBLE) {
      return "CSD"
    }
    return "NO_DEDUCTIBLE"
  })()

  const shouldCollectPartnersCoverageDates = Boolean(
    memberHasLegalPartner && partnerHasInsurance && csdConfiguration !== CSD_CONFIGURATION.MEMBER_AND_PARTNER_COMBINED
  )
  const shouldSkipPartnerCoverageDate = partnerCoverageStartDate || partnerCoverageEndDate
  const shouldSkipMemberCoverageDate = memberCoverageStartDate || memberCoverageEndDate
  const shouldSkipPeekEnrollment = ((): boolean => {
    if (deductibleFlowType === "CSD") {
      if (shouldCollectPartnersCoverageDates) {
        return shouldSkipPartnerCoverageDate
      }
      return shouldSkipMemberCoverageDate
    }
    // This might be able to stay, but we might want to consider revisiting
    // When we fix the enrollment trigger logic
    //
    return Boolean(deductibleFlowType)
  })()

  return (
    <BenefitEnrollmentContext.Provider
      value={{
        ...insuranceSyncInfo,
        companyHasCSDEnabled: sourceName === "CarrotSpecificDeductibleSource",
        companyIsExternalOnly: sourceName === "ChangeHealthcareSource",
        shouldShowDeductibleStatus: csdConfiguration !== CSD_CONFIGURATION.NEITHER,
        shouldSkipPartnerCoverageDate,
        shouldSkipMemberCoverageDate,
        shouldCollectPartnersCoverageDates,
        deductibleFlowType,
        shouldSkipPeekEnrollment,
        memberHasInsurance,
        companyHasExternalDeductible,
        partnerHasInsurance
      }}
    >
      {children}
    </BenefitEnrollmentContext.Provider>
  )
}
